import {cdnImage} from '@/util/cdnImage';
import React, {memo} from 'react';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import Box from '@mui/material/Box';
import Link from 'next/link';

import MUIContainer from '@/components/MUI-Components/MUI-Container';
import MUIGrid from '@/components/MUI-Components/MUI-Grid';
import MUITypography from '@/components/MUI-Components/MUI-Typography';

import {colors, fontFamily, fontSize, spacing, zIndex} from '@/styles/tokens';

const ArrowIcon = cdnImage('career/arrow.webp');
const check = cdnImage('Home-page/check.svg');
const circle = cdnImage('banner/circle.webp');
const bgImg = cdnImage('banner/how-to.svg');

const DisplayImage = dynamic(() => import('@/components/Ui-components/display-image'), {
    ssr: false
});

const ArrowForwardIcon = dynamic(() => import('@mui/icons-material/ArrowForward'), {
    ssr: false
});

import clutchBadge from 'public/images/review-badges/Clutch-Review-bagdes-logo.webp';
import goodfirmsBadge from 'public/images/review-badges/Goodfirms-Review-bagdes-logo.webp';
import topDevBadge from 'public/images/review-badges/Top-Developer-bagdes-logo.webp';

// ─────────────────────────────────────────────────────────────────────────────
// SX CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

const bannerGradientSx = {
    background: `
        radial-gradient(circle at 30% 50%, rgba(222, 235, 247, 0.9), transparent 50%),
        radial-gradient(circle at 70% 50%, rgba(255, 230, 224, 0.9), transparent 50%)
    `,
    backgroundColor: colors.background.default
} as const;

const hiddenSpanSx: React.CSSProperties = {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0,0,0,0)',
    whiteSpace: 'nowrap',
    border: 0
};

// Migrated from `_header.scss`'s `.banner-main-outer` (+ largeScreen/1199px tier — the
// ipadScreen/992px tier restated the identical height:auto/min-height:auto value, a
// fully redundant no-op subset of the 1199px tier, omitted), `.banner-heading` (+ span,
// + 375px and mobileScreen/430px tiers — the 375px tier's font-size:35px duplicates the
// 430px tier for both the heading and its span and is omitted; only the span's unique
// color:#9feafd override at 375px is kept), `.banner-subheading` + `.banner-contact-
// subheading` (same Box, both classes), `.header-text` (+ mobileScreen tier, which
// restates the same 18px value with !important added — preserved literally),
// `.top-banner` (+ nested img, scoped to the direct-child image only so it doesn't also
// match the circleAnimation images nested deeper in the same Box), `.banner-img-outer`
// (tabScreen tier only — the bare, page-ancestor-free rule, plus the `.private-banner-wrapper`/
// `.banner-image-center` page-ancestor overrides — see `bannerImgOuterSx` below),
// `.left-banner-content`, `.banner-points-item-wrapper` (+ img), `.background-color-banner`
// (+ img), `.circle-banner-rotator` (+ img), `.banner-points-content` (+ h4 — see
// `bannerPointsContentSx` below) — all confirmed exclusive to this component via exact-token
// grep across the whole repo. `.app-bar-main` (shared with 6 layout components),
// `.banner-contact-btn` (shared with Ui-components/opportunity), and `.banner-img` (documented
// as "the shared banner-img" — used by ServiceAdvantageImage, Decoding, Dynamic-screens, and 4
// pages) looked like component defaults but are genuinely shared — left global, out of scope for
// this pass. The remaining 3 live page-ancestor overrides (solana-blockchain-development,
// real-world-asset-tokenization, career) and the dead/orphaned selectors found during
// revalidation (`.banner-button`, `.right-space`, a duplicate `.banner-heading .banner-heading
// span` typo, and 2 orphaned `.about-us-main-parent` rules that target a different component)
// also stay untouched — not this component's own defaults.
const bannerMainOuterDefaultSx = {
    paddingTop: '30px !important',
    paddingBottom: '30px !important',
    display: 'flex',
    alignItems: 'center',
    backgroundSize: 'cover !important',
    backgroundRepeat: 'no-repeat !important',
    minHeight: 'calc(100vh - 130px)',
    '@media (max-width: 1199px)': {
        height: 'auto !important',
        minHeight: 'auto !important'
    }
} as const;

const bannerHeadingDefaultSx = {
    fontSize: fontSize[46],
    textAlign: 'left',
    paddingRight: 0,
    letterSpacing: '1px !important',
    '& span': {
        color: `${colors.accent.orange} !important`,
        fontSize: fontSize[46],
        letterSpacing: '1px !important',
        '@media (max-width: 430px)': {
            fontSize: '35px !important' // no exact token match — preserved for parity
        },
        '@media (max-width: 375px)': {
            color: '#9feafd !important' // no token match — preserved for parity
        }
    },
    '@media (max-width: 430px)': {
        fontSize: '35px !important' // no exact token match — preserved for parity
    }
} as const;

const bannerSubheadingContactSx = {
    paddingRight: '65px !important',
    paddingTop: spacing[20],
    '@media (max-width: 430px)': {
        paddingRight: '0px !important'
    }
} as const;

const headerTextDefaultSx = {
    fontSize: fontSize[18],
    textAlign: 'left',
    lineHeight: '30px !important', // no exact lineHeight token (scale has no 30) — preserved for parity
    '@media (max-width: 430px)': {
        fontSize: `${fontSize[18]} !important`
    }
} as const;

const topBannerDefaultSx = {
    float: 'right',
    width: '100%',
    textAlign: 'center',
    '& > img': {
        width: '603px',
        height: 'auto !important'
    }
} as const;

// The `'.private-banner-wrapper &'` / `'.banner-image-center &'` keys migrate globals.scss
// `.private-banner-wrapper .banner-img-outer` (line 3440) and `.banner-image-center
// .banner-img-outer` (line 3576). Both ancestor classNames are literals this component's own JSX
// appends to the ancestor Box's className via the `bannerBackground` prop (see
// `className={`banner-main-outer ${bannerBackground}`}` below — private-blockchain-development
// and mpc-crypto-wallet are the only two live callers passing those exact values), so both are
// reachable via the ancestor-combinator key, exactly mirroring the original cascade with zero
// behavior change and no new prop.
const bannerImgOuterSx = {
    '@media (max-width: 768px)': {
        margin: 'auto !important'
    },
    '.private-banner-wrapper &': {
        display: 'flex !important',
        alignItems: 'center !important'
    },
    '.banner-image-center &': {
        display: 'flex !important',
        alignItems: 'center !important'
    }
} as const;

// Migrated from globals.scss `.banner-points-content h4` (sole consumer). Nested selector carried
// over unchanged: no <h4> is currently rendered inside this Box (both children are
// `<Typography component="p">` / default-variant `<Typography>`), so the rule is currently dead
// but preserved verbatim per the "no current consumer" policy rather than deleted.
const bannerPointsContentSx = {
    '& h4': {
        fontFamily: `${fontFamily.heading} !important`
    }
} as const;

const leftBannerContentSx = {
    '@media (max-width: 768px)': {
        paddingTop: '10px !important',
        paddingBottom: '10px !important'
    },
    '@media (max-width: 1024px)': {
        paddingBottom: `${spacing[12]} !important`
    }
} as const;

const bannerPointsItemWrapperSx = {
    display: 'flex !important',
    gap: `${spacing[8]} !important`,
    '& img': {
        width: '20px !important',
        height: '20px !important',
        paddingTop: '3px !important'
    }
} as const;

const backgroundColorBannerSx = {
    position: 'absolute',
    top: '2%',
    right: '0%',
    '& img': {
        width: '500px !important',
        // Incidental value previously supplied by `.top-banner img`'s broad descendant
        // match — preserved now that top-banner's own img rule is direct-child-scoped.
        height: 'auto'
    },
    '@media (max-width: 768px)': {
        display: 'none !important'
    }
} as const;

const circleBannerRotatorSx = {
    position: 'absolute',
    zIndex: 2,
    animationName: 'MoveCircle360',
    animationTimingFunction: 'linear',
    animationDuration: '10s',
    animationDelay: '0s',
    right: '0%',
    top: '25%',
    animationIterationCount: 'infinite',
    '& img': {
        maxWidth: '100% !important',
        width: '250px !important',
        height: 'auto' // see backgroundColorBannerSx comment
    },
    '@media (max-width: 1024px)': {
        '& img': {
            width: '200px !important'
        }
    },
    '@media (max-width: 768px)': {
        display: 'none !important'
    }
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// REVIEW LINKS
// ─────────────────────────────────────────────────────────────────────────────

const reviewLinks = [
    {
        image: clutchBadge,
        alt: 'Clutch Review Badge',
        href: 'https://clutch.co/profile/debut-infotech#reviews',
        hiddenText: 'Review us on Clutch - Debut Infotech'
    },
    {
        image: goodfirmsBadge,
        alt: 'GoodFirms Review Badge',
        href: 'https://www.goodfirms.co/company/debut-infotech',
        hiddenText: 'Review us on GoodFirms - Debut Infotech'
    },
    {
        image: topDevBadge,
        alt: 'TopDevelopers Review Badge',
        href: 'https://www.topdevelopers.co/profile/debut-infotech',
        hiddenText: 'TopDevelopers - Debut Infotech'
    }
];

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

const Banner = ({
    image,
    handleClickOpen,
    heading,
    subheading,
    button,
    bannerClass,
    className,
    bannerBackground,
    subheading2,
    arrowVisible,
    DataPoints,
    bannerIcons,
    circleAnimation,
    meetingLink,
    priority = false,
    headingLevel = 'h1',
    bannerImageBoxSx
}: any) => {
    return (
        <Grid item xs={12} className="banner-section">
            <Box className="app-bar-main">
                <Box className={`banner-main-outer ${bannerBackground}`} sx={{...bannerMainOuterDefaultSx, ...bannerGradientSx}}>
                    <MUIContainer>
                        <Box className="banner-wrapper">
                            <Grid container spacing={2} justifyContent="center">
                                {/* LEFT CONTENT */}
                                <Grid item sm={12} xs={12} md={6} alignSelf="center" className="banner-left-grid">
                                    <Box className="left-banner-content" sx={leftBannerContentSx}>
                                        <MUITypography
                                            variant="h1"
                                            component={headingLevel}
                                            className="banner-heading font-headers"
                                            sx={bannerHeadingDefaultSx}
                                        >
                                            {heading}
                                        </MUITypography>

                                        <Box className="banner-subheading banner-contact-subheading" sx={bannerSubheadingContactSx}>
                                            <MUITypography
                                                variant="subtitle"
                                                component="p"
                                                className="header-text"
                                                sx={headerTextDefaultSx}
                                            >
                                                {subheading}
                                            </MUITypography>
                                        </Box>

                                        {subheading2 && (
                                            <Box className="banner-subheading banner-contact-subheading" sx={bannerSubheadingContactSx}>
                                                <MUITypography variant="subtitle" className="header-text" sx={headerTextDefaultSx}>
                                                    {subheading2}
                                                </MUITypography>
                                            </Box>
                                        )}

                                        {button && (
                                            <Box className="banner-contact-btn">
                                                {meetingLink ? (
                                                    <Link target="_blank" href={handleClickOpen}>
                                                        <Button
                                                            endIcon={<ArrowForwardIcon />}
                                                            className="button-bg-primary animated-button moving-icon-button"
                                                            variant="contained"
                                                        >
                                                            {button}
                                                        </Button>
                                                    </Link>
                                                ) : (
                                                    <Button
                                                        endIcon={<ArrowForwardIcon />}
                                                        className="button-bg-primary animated-button moving-icon-button"
                                                        variant="contained"
                                                        onClick={handleClickOpen}
                                                    >
                                                        {button}
                                                    </Button>
                                                )}
                                            </Box>
                                        )}

                                        {DataPoints && (
                                            <MUIGrid container className="banner-point-wrapper" pt={3}>
                                                {DataPoints?.map((item: any, index: number) => (
                                                    <MUIGrid
                                                        item
                                                        md={6}
                                                        sm={6}
                                                        xs={6}
                                                        key={index + 1}
                                                        className="banner-points-item-wrapper"
                                                        pb={2}
                                                        sx={bannerPointsItemWrapperSx}
                                                    >
                                                        <DisplayImage src={check} alt="white-check" priority={priority} />

                                                        <Box className="banner-points-content" sx={bannerPointsContentSx}>
                                                            <Typography fontWeight="bolder" component="p">
                                                                {item?.heading}
                                                            </Typography>

                                                            <Typography>{item?.subheading}</Typography>
                                                        </Box>
                                                    </MUIGrid>
                                                ))}
                                            </MUIGrid>
                                        )}
                                    </Box>

                                    {arrowVisible && (
                                        <Box className="banner-icon">
                                            <DisplayImage src={ArrowIcon} alt="arrow-icon" priority={priority} />
                                        </Box>
                                    )}
                                </Grid>

                                {/* RIGHT IMAGE */}
                                <Grid item sm={6} xs={12} md={6} className="banner-img-outer" position="relative" sx={bannerImgOuterSx}>
                                    {image && (
                                        <Box
                                            className={`top-banner ${bannerClass || ''}`}
                                            sx={{...topBannerDefaultSx, ...bannerImageBoxSx}}
                                        >
                                            <Image
                                                src={image}
                                                alt={heading}
                                                className={`banner-img ${className}`}
                                                width={654}
                                                height={532}
                                                priority={priority}
                                                placeholder={
                                                    typeof image === 'object' && 'blurDataURL' in image && image.blurDataURL
                                                        ? 'blur'
                                                        : undefined
                                                }
                                            />

                                            {circleAnimation && (
                                                <Box>
                                                    <Box className="circle-banner-rotator" sx={circleBannerRotatorSx}>
                                                        <Image src={circle} alt="circle Image" />
                                                    </Box>

                                                    <Box className="background-color-banner" sx={backgroundColorBannerSx}>
                                                        <Image src={bgImg} alt="bg-image" />
                                                    </Box>
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </Grid>
                            </Grid>
                        </Box>

                        {/* REVIEW BADGES */}
                        {bannerIcons && (
                            <Box
                                pt={5}
                                pb={3}
                                sx={{
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    gap: {xs: 2, md: 5},
                                    flexWrap: 'wrap',
                                    position: 'relative',
                                    zIndex: zIndex.overlay,
                                    mt: 4
                                }}
                            >
                                {reviewLinks.map((item, index) => (
                                    <a
                                        key={index + 1}
                                        href={item.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            textDecoration: 'none'
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: {xs: '130px', md: '240px'},
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center'
                                            }}
                                        >
                                            <Image
                                                src={item.image}
                                                alt={item.alt}
                                                width={240}
                                                height={70}
                                                priority={priority}
                                                style={{
                                                    width: '100%',
                                                    height: 'auto',
                                                    objectFit: 'contain',
                                                    display: 'block'
                                                }}
                                            />
                                        </Box>

                                        <span style={hiddenSpanSx}>{item.hiddenText}</span>
                                    </a>
                                ))}
                            </Box>
                        )}
                    </MUIContainer>
                </Box>
            </Box>
        </Grid>
    );
};

export default memo(Banner);
